import React, { useMemo } from "react";
import { Box, Text, Spinner, Icon, Button } from "dash-ui";
import { Media } from "gql/generated.graphql";
import {
  DEFAULT_ROOT_MEDIA_FOLDER_NAME,
  SystemMediaFoldersTypesEnum,
} from "dash-common";
import styled, { useTheme } from "styled-components";

interface FolderBrowserProps {
  folders: Media[];
  loading?: boolean;
  onFolderSelect?: (folderId: string) => void;
  onFolderNavigate?: (folderId: string | null) => void;
  selectedFolderId?: string | null;
  currentFolderId?: string | null;
}

const FolderList = styled.div`
  height: 400px;
  overflow-y: auto;
  border: 1px solid ${({ theme }) => theme.colors.n20};
  border-radius: 4px;
  padding: 4px;
  background-color: ${({ theme }) => theme.colors.n5};
`;

const FolderItem = styled.div<{ isSelected?: boolean; isBackButton?: boolean }>`
  display: flex;
  align-items: center;
  padding: 10px 12px;
  cursor: pointer;
  border-radius: 4px;
  margin-bottom: 2px;
  background-color: ${({ isSelected, isBackButton, theme }) =>
    isSelected
      ? theme.colors.b10
      : isBackButton
      ? "transparent"
      : theme.colors.white};
  border: 1px solid
    ${({ isSelected, theme }) =>
      isSelected ? theme.colors.b30 : "transparent"};

  &:hover {
    background-color: ${({ isBackButton, theme }) =>
      isBackButton ? theme.colors.n10 : theme.colors.n10};
  }
`;

const FolderIconWrapper = styled.span`
  margin-right: 12px;
  display: inline-flex;
  align-items: center;
`;

const FolderName = styled(Text)`
  font-weight: 500;
`;

const FolderBadge = styled.span`
  font-size: 11px;
  padding: 2px 6px;
  border-radius: 4px;
  background-color: ${({ theme }) => theme.colors.g10};
  color: ${({ theme }) => theme.colors.g20};
  margin-left: 4px;
  font-weight: 500;
`;

const FolderBrowser: React.FC<FolderBrowserProps> = ({
  folders,
  loading = false,
  onFolderSelect,
  onFolderNavigate,
  selectedFolderId,
  currentFolderId,
}) => {
  const theme = useTheme();

  // Find the root folder
  const rootFolder = folders.find(
    (f) => f.folderName === DEFAULT_ROOT_MEDIA_FOLDER_NAME
  );
  const rootFolderId = rootFolder?._id || null;

  // Find the current folder object to get its parent
  const currentFolder = useMemo(() => {
    if (!currentFolderId) return null;
    return folders.find((f) => f._id === currentFolderId);
  }, [currentFolderId, folders]);

  // Get parent folder ID for back navigation
  const parentFolderId = currentFolder?.mediaFolderId || null;

  // Build folder hierarchy - get children of current folder
  const visibleFolders = useMemo(() => {
    if (!folders || folders.length === 0) return [];

    // Find the root folder
    const rootFolder = folders.find(
      (f) => f.folderName === DEFAULT_ROOT_MEDIA_FOLDER_NAME
    );
    const rootFolderId = rootFolder?._id;

    return folders
      .filter((folder) => {
        // Filter out the root folder itself from display
        if (folder.folderName === DEFAULT_ROOT_MEDIA_FOLDER_NAME) {
          return false;
        }

        if (!currentFolderId) {
          // At root level - show only top-level folders (direct children of root)
          // Check if the folder's direct parent is the root folder
          return (
            folder.mediaFolderId === rootFolderId ||
            (!folder.mediaFolderId &&
              (!folder.parentMediaFoldersIds ||
                folder.parentMediaFoldersIds.length <= 1))
          );
        }

        // Show folders that are direct children of current folder
        return folder.mediaFolderId === currentFolderId;
      })
      .sort((a, b) => {
        // Sort system folders first, then alphabetically
        if (a.systemFolderType && !b.systemFolderType) return -1;
        if (!a.systemFolderType && b.systemFolderType) return 1;
        return (a.folderName || "").localeCompare(b.folderName || "");
      });
  }, [folders, currentFolderId]);

  const handleFolderClick = (folder: Media) => {
    // Check if folder has children
    const hasChildren = folders.some((f) => f.mediaFolderId === folder._id);

    if (hasChildren && onFolderNavigate) {
      // Navigate into folder if it has children
      onFolderNavigate(folder._id);
    }

    // Always allow selection, even for folders with children
    if (onFolderSelect) {
      onFolderSelect(folder._id);
    }
  };

  // Build breadcrumbs path - MUST be before any conditional returns
  const breadcrumbs = useMemo(() => {
    const path: string[] = ["Root"];
    if (currentFolderId && currentFolder) {
      // Add parent folders from parentMediaFoldersIds
      if (currentFolder.parentMediaFoldersIds) {
        currentFolder.parentMediaFoldersIds.forEach((parentId) => {
          if (parentId !== rootFolderId) {
            const parent = folders.find((f) => f._id === parentId);
            if (
              parent &&
              parent.folderName !== DEFAULT_ROOT_MEDIA_FOLDER_NAME
            ) {
              path.push(parent.folderName || "");
            }
          }
        });
      }
      path.push(currentFolder.folderName || "");
    }
    return path;
  }, [currentFolder, currentFolderId, folders, rootFolderId]);

  const handleSelectCurrentFolder = () => {
    if (onFolderSelect) {
      onFolderSelect(currentFolderId || "");
    }
  };

  const getSystemFolderBadge = (systemFolderType?: string) => {
    // Show badge for both Images and Slideshow folders
    if (
      systemFolderType === SystemMediaFoldersTypesEnum.Images ||
      systemFolderType === SystemMediaFoldersTypesEnum.Slideshow
    ) {
      return "Shared folder";
    }
    return null;
  };

  if (loading) {
    return (
      <Box
        style={{
          height: "200px",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <Spinner />
      </Box>
    );
  }

  return (
    <Box>
      {/* Navigation and action buttons */}
      <Box mb={2} style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
        {/* Back navigation button */}
        <Button
          type="button"
          variation="outline"
          medium
          onClick={() => onFolderNavigate && onFolderNavigate(parentFolderId)}
          icon="chevronLeft"
          disabled={!currentFolderId}
        >
          Back
        </Button>

        {/* Select current folder button */}
        {currentFolderId ? (
          <Button
            type="button"
            variation="primary"
            medium
            onClick={handleSelectCurrentFolder}
            icon="check"
            disabled={selectedFolderId === currentFolderId}
          >
            Select Current Folder
          </Button>
        ) : (
          // At root level - allow selecting root folder
          <Button
            type="button"
            variation="primary"
            medium
            onClick={() => onFolderSelect && onFolderSelect(rootFolderId || "")}
            icon="check"
            disabled={selectedFolderId === rootFolderId}
          >
            Select Root Folder
          </Button>
        )}
      </Box>

      {/* Breadcrumbs */}
      <Box mb={3}>
        <Text color={theme.colors.n60}>
          <small>{breadcrumbs.join(" / ")}</small>
        </Text>
      </Box>

      {/* Folder list */}
      <FolderList>
        {visibleFolders.length === 0 ? (
          <Box
            style={{
              height: "100px",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            <Text color="n60">No folders available</Text>
          </Box>
        ) : (
          visibleFolders.map((folder) => {
            const hasChildren = folders.some(
              (f) => f.mediaFolderId === folder._id
            );
            const badge = getSystemFolderBadge(
              folder.systemFolderType || undefined
            );

            return (
              <FolderItem
                key={folder._id}
                isSelected={selectedFolderId === folder._id}
                onClick={() => handleFolderClick(folder)}
              >
                <FolderIconWrapper>
                  <Icon color={theme.colors.n60} name="folder" size={20} />
                </FolderIconWrapper>
                <FolderName>{folder.folderName || ""}</FolderName>
                {badge && <FolderBadge>{badge}</FolderBadge>}
                {hasChildren && (
                  <Box style={{ marginLeft: "8px" }}>
                    <Icon
                      color={theme.colors.n60}
                      name="chevronRight"
                      size={16}
                    />
                  </Box>
                )}
              </FolderItem>
            );
          })
        )}
      </FolderList>
    </Box>
  );
};

export default FolderBrowser;
